import { FormInstance } from 'antd/lib/form'
import {
    AttributeDefinitionType,
    AttributesData,
    TaskAttribute,
    UpdatedAttributesData,
} from 'Types/Tasks.types'
import { AttributeContext } from '../AttributeManager'
import { UpdatableReactElement } from 'shared/utils/react.utils'
import { ReactElement } from 'react'

export type AttributeGroupConfig<
    TTaskProps extends Record<string, unknown> = Record<string, unknown>,
> = {
    useInCollection: boolean
    isDisabled?: boolean
    props?: {
        header?: ReactElement | string
    }
    taskProps?: TTaskProps
}

export type AttributeGroupConfigExtended<
    TTaskProps extends Record<string, unknown> = Record<string, unknown>,
> = AttributeGroupConfig<TTaskProps> & {
    groupKey: string
    attributes: TaskAttribute[]
}

export enum AttributeGroupEvent {
    Change = 'change',
}

export interface IAttributeGroup {
    useInCollection?: boolean
    props?: AttributeGroupConfig['props']
    taskProps?: AttributeGroupConfig['taskProps']
    groupKey: string
    attributeDefinitionType: AttributeDefinitionType
    areMandatoryAttributesFilled: boolean
    build(): void
    validate(): Promise<boolean>
    getElement(): UpdatableReactElement | undefined
    render(): void
    on(event: AttributeGroupEvent, callback: () => void): void
    off(event: AttributeGroupEvent, callback: () => void): void
    emit(event: AttributeGroupEvent): void
    disable(): void
    enable(): void
    destroy(): void
    readonly attributes: TaskAttribute[]
}

export interface IAttributeGroupConstructor {
    new (
        attributeContext: AttributeContext,
        config: AttributeGroupConfigExtended<Record<string, unknown>>
    ): IAttributeGroup
}

export abstract class BaseAttributeGroup<
    TTaskProps extends Record<string, unknown> = Record<string, unknown>,
> implements IAttributeGroup
{
    groupKey: string
    protected form?: FormInstance
    protected attributeContext: AttributeContext
    useInCollection: boolean = false
    props?: AttributeGroupConfig['props']
    taskProps?: TTaskProps
    protected element?: UpdatableReactElement
    private eventListeners: Map<AttributeGroupEvent, (() => void)[]> = new Map()
    abstract attributeDefinitionType: AttributeDefinitionType
    abstract build(): void
    abstract validate(): Promise<boolean>
    attributes: TaskAttribute[] = []
    areMandatoryAttributesFilled: boolean = false
    disabled: boolean = false

    constructor(attributeContext: AttributeContext, config: AttributeGroupConfigExtended) {
        this.attributeContext = attributeContext
        this.useInCollection = config.useInCollection
        this.props = config.props
        this.groupKey = config.groupKey
        this.taskProps = config.taskProps as TTaskProps
        this.attributes = config.attributes
    }

    abstract removeAllAttributes(): void

    get mandatoryAttributes(): TaskAttribute[] {
        return this.attributes.filter(attr => !attr.input_definition.is_optional)
    }

    on(event: AttributeGroupEvent, callback: () => void): void {
        if (!this.eventListeners.has(event)) {
            this.eventListeners.set(event, [])
        }
        this.eventListeners.get(event)!.push(callback)
    }

    off(event: AttributeGroupEvent, callback: () => void): void {
        const listeners = this.eventListeners.get(event) || []
        this.eventListeners.set(
            event,
            listeners.filter(cb => cb !== callback)
        )
    }

    emit(event: AttributeGroupEvent): void {
        const listeners = this.eventListeners.get(event) || []
        listeners.forEach(callback => callback())
    }

    getElement(): UpdatableReactElement | undefined {
        return this.element
    }

    render() {
        this.element?.updater.forceUpdate()
    }

    updateAreMandatoryAttributesFilled(attributesData?: UpdatedAttributesData | AttributesData) {
        if (!this.mandatoryAttributes.length) {
            this.areMandatoryAttributesFilled = true
            return
        }

        if (!attributesData) {
            this.areMandatoryAttributesFilled = false
            return
        }

        this.areMandatoryAttributesFilled = !this.mandatoryAttributes.some(attr => {
            if (!attributesData[attr.name]) {
                return !!attr.visibility_condition
            }
            if ('data' in attributesData[attr.name]) {
                return !(attributesData as AttributesData)[attr.name]?.data?.value
            }
            if ('value' in attributesData[attr.name]) {
                return !(attributesData as UpdatedAttributesData)[attr.name].value
            }
            return true
        })
    }

    disable() {
        this.disabled = true
        this.render()
    }

    enable() {
        this.disabled = false
        this.render()
    }

    destroy() {
        this.element = undefined
        this.eventListeners.clear()
        this.removeAllAttributes()
        this.render()
    }
}
